// ============================================
// 오링왕국 - 이벤트 데이터 (10시간+ 분량)
// ============================================

import { GameEvent, Era, Season, BuildingType, EndingType } from '../types';

// ============================================
// 체인 이벤트 정의
// ============================================

export interface ChainDefinition {
    id: string;
    name: string;
    description: string;
    totalSteps: number;
    unlockCondition?: string;
}

export const CHAIN_DEFINITIONS: Record<string, ChainDefinition> = {
    // 메인 스토리 체인
    fox_invasion: {
        id: 'fox_invasion',
        name: '여우의 침공',
        description: '여우 군단이 왕국을 위협합니다. 전쟁인가, 협상인가?',
        totalSteps: 8
    },
    mysterious_egg: {
        id: 'mysterious_egg',
        name: '수상한 알',
        description: '연못에서 발견된 황금빛 알. 무엇이 태어날까?',
        totalSteps: 6
    },
    sky_migration: {
        id: 'sky_migration',
        name: '하늘로의 여정',
        description: '철새들과 함께 새로운 세상을 찾아 떠나는 모험.',
        totalSteps: 7
    },
    sea_expedition: {
        id: 'sea_expedition',
        name: '심해 탐험',
        description: '바다 건너 미지의 세계로. 해적 펠리컨과의 만남.',
        totalSteps: 8
    },
    sage_teachings: {
        id: 'sage_teachings',
        name: '현자의 가르침',
        description: '고대 오리 현자에게서 지혜를 배웁니다.',
        totalSteps: 5
    },
    merchant_empire: {
        id: 'merchant_empire',
        name: '상인의 길',
        description: '거위 상인과 함께 무역 제국을 건설합니다.',
        totalSteps: 6
    },
    rebel_uprising: {
        id: 'rebel_uprising',
        name: '참새의 반란',
        description: '불만을 품은 참새들이 반란을 일으킵니다.',
        totalSteps: 5
    },
    ancient_secret: {
        id: 'ancient_secret',
        name: '고대의 비밀',
        description: '거북이 장로가 전하는 잊혀진 역사.',
        totalSteps: 7
    },
    royal_wedding: {
        id: 'royal_wedding',
        name: '왕실 결혼식',
        description: '공작 왕국과의 정략 결혼. 동맹인가, 함정인가?',
        totalSteps: 5
    },
    shadow_conspiracy: {
        id: 'shadow_conspiracy',
        name: '그림자 음모',
        description: '까마귀들의 비밀 조직. 그들의 목적은?',
        totalSteps: 6
    },
    true_path: {
        id: 'true_path',
        name: '진실의 길',
        description: '모든 것의 진실을 찾아가는 여정.',
        totalSteps: 10
    },
    secret_duck: {
        id: 'secret_duck',
        name: '비밀의 오리',
        description: '전설 속 황금 오리를 찾아서.',
        totalSteps: 8
    }
};

// ============================================
// 일상 이벤트 (Daily Events)
// ============================================

const DAILY_EVENTS: GameEvent[] = [
    // 초반 이벤트 (연못 시대)
    {
        id: 'daily_bread_crumbs',
        title: '빵 부스러기 발견!',
        description: '연못가에 누군가 빵 부스러기를 뿌려놓았습니다. 오리들이 신나합니다!',
        category: 'daily',
        maxEra: Era.VILLAGE,
        weight: 20,
        options: [
            {
                id: 'bread_eat',
                text: '모두 함께 먹는다',
                moneyChange: 10,
                popChange: 0,
                happinessChange: 5,
                outcomeDescription: '배부른 오리는 행복한 오리!'
            },
            {
                id: 'bread_save',
                text: '일부를 저장한다',
                moneyChange: 20,
                popChange: 0,
                happinessChange: -2,
                outcomeDescription: '현명한 선택이지만 조금 아쉬워하는 오리들...'
            }
        ]
    },
    {
        id: 'daily_sunny_day',
        title: '화창한 날',
        description: '오늘은 날씨가 정말 좋습니다. 오리들이 연못에서 헤엄치고 싶어합니다.',
        category: 'daily',
        weight: 25,
        options: [
            {
                id: 'sunny_swim',
                text: '오후 휴식 시간을 준다',
                moneyChange: -5,
                popChange: 0,
                happinessChange: 10,
                outcomeDescription: '꽥꽥! 즐거운 물놀이 시간!'
            },
            {
                id: 'sunny_work',
                text: '날씨 좋을 때 일하자',
                moneyChange: 15,
                popChange: 0,
                happinessChange: -3,
                outcomeDescription: '생산성은 올랐지만 오리들이 투덜거립니다.'
            }
        ]
    },
    {
        id: 'daily_feather_fashion',
        title: '깃털 패션',
        description: '어떤 오리가 멋진 깃털 장식을 하고 나타났습니다. 유행이 시작될 조짐!',
        category: 'daily',
        weight: 15,
        options: [
            {
                id: 'fashion_support',
                text: '깃털 장식을 장려한다',
                moneyChange: -30,
                popChange: 0,
                happinessChange: 8,
                outcomeDescription: '화려한 오리 왕국! 관광객이 늘었습니다.'
            },
            {
                id: 'fashion_ban',
                text: '사치스럽다며 금지한다',
                moneyChange: 10,
                popChange: 0,
                happinessChange: -5,
                outcomeDescription: '절약 정신! 하지만 오리들이 시무룩합니다.'
            }
        ]
    },
    {
        id: 'daily_egg_count',
        title: '알 점검의 날',
        description: '둥지마다 알이 몇 개인지 세어봅니다. 인구 조사의 시작!',
        category: 'daily',
        weight: 30,
        minEra: Era.VILLAGE,
        options: [
            {
                id: 'egg_celebrate',
                text: '출산을 장려한다',
                moneyChange: -50,
                popChange: 8,
                happinessChange: 5,
                outcomeDescription: '아기 오리들이 태어났습니다! 꽥꽥!'
            },
            {
                id: 'egg_moderate',
                text: '적정 수준 유지',
                moneyChange: 0,
                popChange: 2,
                happinessChange: 0,
                outcomeDescription: '안정적인 인구 성장입니다.'
            }
        ]
    },
    {
        id: 'daily_pond_cleaning',
        title: '연못 청소',
        description: '연못이 좀 더러워졌습니다. 청소할까요?',
        category: 'daily',
        weight: 18,
        options: [
            {
                id: 'clean_all',
                text: '대청소를 한다',
                moneyChange: -40,
                popChange: 0,
                happinessChange: 15,
                outcomeDescription: '반짝반짝! 깨끗한 연못에서 행복한 오리들!'
            },
            {
                id: 'clean_partial',
                text: '대충 치운다',
                moneyChange: -10,
                popChange: 0,
                happinessChange: 3,
                outcomeDescription: '그럭저럭 살만합니다.'
            },
            {
                id: 'clean_skip',
                text: '자연에 맡긴다',
                moneyChange: 0,
                popChange: 0,
                happinessChange: -5,
                outcomeDescription: '냄새가 좀 나지만... 자연스러운 거겠죠?'
            }
        ]
    },
    {
        id: 'daily_worm_shortage',
        title: '지렁이 부족',
        description: '최근 비가 안 와서 지렁이가 부족합니다.',
        category: 'daily',
        weight: 12,
        options: [
            {
                id: 'worm_import',
                text: '외부에서 수입한다',
                moneyChange: -80,
                popChange: 0,
                happinessChange: 5,
                outcomeDescription: '맛있는 지렁이 확보! 배고픔 해결!'
            },
            {
                id: 'worm_diet',
                text: '배급을 줄인다',
                moneyChange: 0,
                popChange: -1,
                happinessChange: -10,
                outcomeDescription: '배고픈 오리들... 일부가 떠났습니다.'
            },
            {
                id: 'worm_alternative',
                text: '다른 먹이를 찾는다',
                moneyChange: -20,
                popChange: 0,
                happinessChange: -2,
                outcomeDescription: '수초도 먹을만 하네요.'
            }
        ]
    },
    {
        id: 'daily_visitor_duck',
        title: '외지 오리의 방문',
        description: '멀리서 온 오리가 정착하고 싶어합니다.',
        category: 'daily',
        weight: 35,
        options: [
            {
                id: 'visitor_welcome',
                text: '환영한다',
                moneyChange: 0,
                popChange: 5,
                happinessChange: 3,
                outcomeDescription: '새 친구들이 왕국에 합류했습니다!'
            },
            {
                id: 'visitor_test',
                text: '시험을 치르게 한다',
                moneyChange: 10,
                popChange: 2,
                happinessChange: 0,
                outcomeDescription: '엄격한 심사 후 일부만 받아들였습니다.'
            },
            {
                id: 'visitor_reject',
                text: '정중히 거절한다',
                moneyChange: 0,
                popChange: 0,
                happinessChange: -2,
                outcomeDescription: '우리끼리 살자...'
            }
        ]
    },
    {
        id: 'daily_duckling_boom',
        title: '새끼 오리 탄생!',
        description: '둥지에서 알들이 부화하고 있습니다! 꽥꽥!',
        category: 'daily',
        weight: 30,
        options: [
            {
                id: 'duckling_celebrate',
                text: '탄생을 축하한다',
                moneyChange: -20,
                popChange: 6,
                happinessChange: 8,
                outcomeDescription: '귀여운 새끼 오리들이 태어났습니다!'
            },
            {
                id: 'duckling_care',
                text: '보육원을 차린다',
                moneyChange: -50,
                popChange: 8,
                happinessChange: 5,
                outcomeDescription: '체계적인 돌봄! 더 많은 새끼가 건강하게 자랍니다.'
            }
        ]
    },
    {
        id: 'daily_refugee_flock',
        title: '피난민 무리',
        description: '근처 연못이 말라서 오리들이 피난왔습니다.',
        category: 'daily',
        weight: 25,
        options: [
            {
                id: 'refugee_accept_all',
                text: '모두 받아들인다',
                moneyChange: -30,
                popChange: 10,
                happinessChange: 5,
                outcomeDescription: '따뜻한 환대! 많은 오리들이 정착했습니다.'
            },
            {
                id: 'refugee_select',
                text: '일부만 받는다',
                moneyChange: 0,
                popChange: 5,
                happinessChange: 0,
                outcomeDescription: '젊고 건강한 오리들만 받아들였습니다.'
            },
            {
                id: 'refugee_refuse',
                text: '돌려보낸다',
                moneyChange: 0,
                popChange: 0,
                happinessChange: -5,
                outcomeDescription: '차가운 거절... 분위기가 냉랭합니다.'
            }
        ]
    },
    {
        id: 'daily_family_reunion',
        title: '오리 가족 상봉',
        description: '헤어졌던 오리 가족들이 찾아왔습니다!',
        category: 'daily',
        weight: 20,
        options: [
            {
                id: 'reunion_welcome',
                text: '따뜻하게 맞이한다',
                moneyChange: 0,
                popChange: 4,
                happinessChange: 10,
                outcomeDescription: '감동적인 재회! 모두가 눈물을 흘립니다.'
            },
            {
                id: 'reunion_party',
                text: '환영 파티를 연다',
                moneyChange: -40,
                popChange: 6,
                happinessChange: 15,
                outcomeDescription: '성대한 파티! 행복이 넘칩니다!'
            }
        ]
    },
    {
        id: 'daily_nest_dispute',
        title: '둥지 분쟁',
        description: '두 오리 가족이 같은 둥지를 두고 싸웁니다.',
        category: 'daily',
        weight: 15,
        options: [
            {
                id: 'dispute_mediate',
                text: '중재한다',
                moneyChange: -20,
                popChange: 0,
                happinessChange: 5,
                outcomeDescription: '공정한 해결! 둘 다 만족합니다.'
            },
            {
                id: 'dispute_first',
                text: '먼저 온 쪽 편을 든다',
                moneyChange: 0,
                popChange: -1,
                happinessChange: -3,
                outcomeDescription: '한쪽이 떠났지만 질서는 지켜졌습니다.'
            },
            {
                id: 'dispute_ignore',
                text: '알아서 하게 둔다',
                moneyChange: 0,
                popChange: 0,
                happinessChange: -8,
                outcomeDescription: '싸움이 격해졌습니다. 분위기 험악...'
            }
        ]
    },
    {
        id: 'daily_fishing_spot',
        title: '물고기 떼 발견',
        description: '연못에 물고기 떼가 나타났습니다! 오리들이 신나합니다.',
        category: 'daily',
        weight: 18,
        options: [
            {
                id: 'fish_feast',
                text: '잔치를 연다',
                moneyChange: -30,
                popChange: 0,
                happinessChange: 12,
                outcomeDescription: '물고기 잔치! 오리들이 춤을 춥니다!'
            },
            {
                id: 'fish_preserve',
                text: '절제하며 먹는다',
                moneyChange: 30,
                popChange: 0,
                happinessChange: 3,
                outcomeDescription: '남은 물고기를 저장했습니다.'
            }
        ]
    },
    {
        id: 'daily_baby_first_swim',
        title: '아기 오리의 첫 수영',
        description: '이번 시즌 태어난 아기 오리들이 처음으로 물에 들어갑니다!',
        category: 'daily',
        weight: 15,
        season: Season.SPRING,
        options: [
            {
                id: 'swim_ceremony',
                text: '성대한 행사로 치른다',
                moneyChange: -50,
                popChange: 0,
                happinessChange: 15,
                outcomeDescription: '감동적인 순간! 온 왕국이 축하합니다!'
            },
            {
                id: 'swim_simple',
                text: '조용히 지켜본다',
                moneyChange: 0,
                popChange: 0,
                happinessChange: 5,
                outcomeDescription: '귀여운 아기 오리들의 첫 발걸음...'
            }
        ]
    },
    {
        id: 'daily_talent_show',
        title: '오리 장기자랑',
        description: '오리들이 자신의 재능을 뽐내고 싶어합니다.',
        category: 'daily',
        weight: 12,
        minEra: Era.VILLAGE,
        options: [
            {
                id: 'talent_support',
                text: '장기자랑 대회 개최',
                moneyChange: -60,
                popChange: 0,
                happinessChange: 15,
                outcomeDescription: '꽥꽥 노래, 물장구 춤! 대성공!'
            },
            {
                id: 'talent_reject',
                text: '일이나 하자',
                moneyChange: 10,
                popChange: 0,
                happinessChange: -5,
                outcomeDescription: '재미없는 왕국...'
            }
        ]
    },
    {
        id: 'daily_rain_dance',
        title: '비를 부르는 춤',
        description: '가뭄이 계속되자 원로 오리가 비를 부르는 춤을 추자고 합니다.',
        category: 'daily',
        weight: 10,
        season: Season.SUMMER,
        options: [
            {
                id: 'rain_dance_yes',
                text: '다 함께 춤을 춘다',
                moneyChange: 0,
                popChange: 0,
                happinessChange: 8,
                outcomeDescription: Math.random() > 0.5
                    ? '믿기 힘들지만... 비가 왔어요!'
                    : '비는 안 왔지만 재미있었어요!'
            },
            {
                id: 'rain_dance_no',
                text: '미신이라며 거절',
                moneyChange: 0,
                popChange: 0,
                happinessChange: -3,
                outcomeDescription: '원로들이 서운해합니다.'
            }
        ]
    },
    {
        id: 'daily_love_confession',
        title: '사랑 고백',
        description: '젊은 오리가 짝사랑하는 오리에게 고백을 앞두고 조언을 구합니다.',
        category: 'daily',
        weight: 10,
        options: [
            {
                id: 'love_encourage',
                text: '용기를 북돋아준다',
                moneyChange: 0,
                popChange: 0,
                happinessChange: 5,
                outcomeDescription: '고백 성공! 새 커플 탄생!'
            },
            {
                id: 'love_practical',
                text: '현실적인 조언을 한다',
                moneyChange: 0,
                popChange: 0,
                happinessChange: 2,
                outcomeDescription: '신중하게 접근하기로 했습니다.'
            }
        ]
    },
    {
        id: 'daily_grandpa_story',
        title: '할아버지 오리의 옛날 이야기',
        description: '가장 나이 많은 오리가 옛날 이야기를 들려주겠다고 합니다.',
        category: 'daily',
        weight: 12,
        options: [
            {
                id: 'story_listen',
                text: '모두 모여 듣는다',
                moneyChange: -5,
                popChange: 0,
                happinessChange: 8,
                outcomeDescription: '전설 속 황금 오리 이야기... 흥미진진!',
                setFlag: 'heard_golden_duck_legend'
            },
            {
                id: 'story_skip',
                text: '다음에 듣자',
                moneyChange: 0,
                popChange: 0,
                happinessChange: -2,
                outcomeDescription: '할아버지가 서운해합니다.'
            }
        ]
    },
    {
        id: 'daily_night_watch',
        title: '야간 경비',
        description: '밤에 이상한 소리가 들립니다. 누가 경비를 설까요?',
        category: 'daily',
        weight: 15,
        options: [
            {
                id: 'watch_volunteer',
                text: '자원자를 모집한다',
                moneyChange: 0,
                popChange: 0,
                happinessChange: 3,
                outcomeDescription: '용감한 오리들이 나섰습니다!'
            },
            {
                id: 'watch_pay',
                text: '급여를 주고 고용한다',
                moneyChange: -30,
                popChange: 0,
                happinessChange: 5,
                outcomeDescription: '전문 경비 오리가 배치되었습니다.'
            },
            {
                id: 'watch_ignore',
                text: '무시한다',
                moneyChange: 0,
                popChange: 0,
                happinessChange: -5,
                outcomeDescription: '불안한 밤이 계속됩니다...'
            }
        ]
    },

    // 중반 이벤트 (마을~도시)
    {
        id: 'daily_market_day',
        title: '장날',
        description: '이번 주 장날입니다. 무엇을 할까요?',
        category: 'daily',
        minEra: Era.VILLAGE,
        weight: 18,
        options: [
            {
                id: 'market_trade',
                text: '물물교환을 활성화',
                moneyChange: 50,
                popChange: 0,
                happinessChange: 5,
                outcomeDescription: '활발한 거래! 경제가 성장합니다.'
            },
            {
                id: 'market_tax',
                text: '세금을 걷는다',
                moneyChange: 80,
                popChange: 0,
                happinessChange: -5,
                outcomeDescription: '세수는 늘었지만 상인들이 투덜거립니다.'
            },
            {
                id: 'market_free',
                text: '자유 시장으로 둔다',
                moneyChange: 20,
                popChange: 2,
                happinessChange: 3,
                outcomeDescription: '자유로운 분위기에 이주민이 늘었습니다.'
            }
        ]
    },
    {
        id: 'daily_school_debate',
        title: '교육 논쟁',
        description: '아기 오리들에게 무엇을 가르칠지 의견이 분분합니다.',
        category: 'daily',
        minEra: Era.VILLAGE,
        weight: 12,
        requiresBuilding: BuildingType.SCHOOL,
        options: [
            {
                id: 'school_practical',
                text: '실용적인 것 (물고기 잡기)',
                moneyChange: 20,
                popChange: 0,
                happinessChange: 0,
                outcomeDescription: '먹고 사는 문제가 우선!'
            },
            {
                id: 'school_arts',
                text: '예술 (꽥꽥 노래)',
                moneyChange: -10,
                popChange: 0,
                happinessChange: 8,
                outcomeDescription: '아름다운 노래가 연못에 울려 퍼집니다.'
            },
            {
                id: 'school_both',
                text: '둘 다 가르친다',
                moneyChange: -30,
                popChange: 0,
                happinessChange: 5,
                outcomeDescription: '균형 잡힌 교육!'
            }
        ]
    },
    {
        id: 'daily_construction_noise',
        title: '공사 소음',
        description: '새 건물 공사 소음에 주민들이 불평합니다.',
        category: 'daily',
        minEra: Era.VILLAGE,
        weight: 10,
        options: [
            {
                id: 'noise_apologize',
                text: '사과하고 보상한다',
                moneyChange: -40,
                popChange: 0,
                happinessChange: 5,
                outcomeDescription: '이해해주셔서 감사합니다!'
            },
            {
                id: 'noise_explain',
                text: '발전을 위해 필요하다고 설명',
                moneyChange: 0,
                popChange: 0,
                happinessChange: -3,
                outcomeDescription: '이해는 하지만 여전히 시끄럽다...'
            },
            {
                id: 'noise_nightwork',
                text: '밤에만 공사한다',
                moneyChange: -20,
                popChange: 0,
                happinessChange: -8,
                outcomeDescription: '잠을 못 자게 되었습니다!'
            }
        ]
    },
    {
        id: 'daily_elder_council',
        title: '원로회의',
        description: '원로 오리들이 중요한 문제에 대해 회의를 요청합니다.',
        category: 'daily',
        minEra: Era.CITY,
        weight: 15,
        options: [
            {
                id: 'council_respect',
                text: '의견을 존중한다',
                moneyChange: 0,
                popChange: 0,
                happinessChange: 5,
                outcomeDescription: '전통을 지키는 현명한 결정!'
            },
            {
                id: 'council_reform',
                text: '개혁이 필요하다고 주장',
                moneyChange: 20,
                popChange: 0,
                happinessChange: -5,
                outcomeDescription: '젊은 오리들은 지지하지만...'
            }
        ]
    },
    {
        id: 'daily_foreign_goods',
        title: '이국의 물건',
        description: '상인이 신기한 물건을 가져왔습니다.',
        category: 'daily',
        minEra: Era.CITY,
        weight: 15,
        options: [
            {
                id: 'foreign_buy',
                text: '비싼 값에 구매한다',
                moneyChange: -100,
                popChange: 0,
                happinessChange: 10,
                outcomeDescription: '신기한 물건에 모두가 감탄합니다!'
            },
            {
                id: 'foreign_reject',
                text: '우리 것이 좋다',
                moneyChange: 0,
                popChange: 0,
                happinessChange: 2,
                outcomeDescription: '전통을 지키는 왕국!'
            },
            {
                id: 'foreign_copy',
                text: '기술을 배워온다',
                moneyChange: -50,
                popChange: 0,
                happinessChange: 5,
                outcomeDescription: '새로운 기술을 습득했습니다!',
                setFlag: 'learned_foreign_tech'
            }
        ]
    },

    // 후반 이벤트 (왕국~제국)
    {
        id: 'daily_royal_inspection',
        title: '왕실 순시',
        description: '왕(당신)이 직접 왕국을 둘러봅니다.',
        category: 'daily',
        minEra: Era.KINGDOM,
        weight: 12,
        options: [
            {
                id: 'inspection_grand',
                text: '성대한 행차로',
                moneyChange: -200,
                popChange: 0,
                happinessChange: 15,
                outcomeDescription: '위엄 있는 왕! 백성들이 환호합니다!'
            },
            {
                id: 'inspection_humble',
                text: '소박하게',
                moneyChange: 0,
                popChange: 0,
                happinessChange: 8,
                outcomeDescription: '친근한 왕! 백성들이 사랑합니다.'
            },
            {
                id: 'inspection_secret',
                text: '변장하고 몰래',
                moneyChange: 0,
                popChange: 0,
                happinessChange: 0,
                outcomeDescription: '백성들의 진짜 생각을 들었습니다.',
                setFlag: 'secret_inspection'
            }
        ]
    },
    {
        id: 'daily_empire_tribute',
        title: '조공 요청',
        description: '주변 작은 연못들이 조공을 바치겠다고 합니다.',
        category: 'daily',
        minEra: Era.EMPIRE,
        weight: 15,
        options: [
            {
                id: 'tribute_accept',
                text: '받아들인다',
                moneyChange: 500,
                popChange: 10,
                happinessChange: 5,
                outcomeDescription: '제국의 영광! 조공이 쏟아집니다.'
            },
            {
                id: 'tribute_alliance',
                text: '동맹으로 받아들인다',
                moneyChange: 100,
                popChange: 20,
                happinessChange: 10,
                outcomeDescription: '평화로운 확장! 동맹국이 늘었습니다.'
            },
            {
                id: 'tribute_reject',
                text: '독립을 유지하게 한다',
                moneyChange: 0,
                popChange: 0,
                happinessChange: 15,
                outcomeDescription: '덕이 높은 왕국이라는 소문이 퍼집니다.'
            }
        ]
    },
    {
        id: 'daily_corruption_report',
        title: '부정부패 제보',
        description: '누군가 고위 오리의 부정을 제보했습니다.',
        category: 'daily',
        minEra: Era.CITY,
        weight: 10,
        options: [
            {
                id: 'corruption_investigate',
                text: '철저히 조사한다',
                moneyChange: -50,
                popChange: 0,
                happinessChange: 10,
                outcomeDescription: '정의 구현! 부패 척결!',
                setFlag: 'anti_corruption'
            },
            {
                id: 'corruption_cover',
                text: '조용히 무마한다',
                moneyChange: 100,
                popChange: 0,
                happinessChange: -15,
                outcomeDescription: '일단 넘어갔지만... 불씨가 남았습니다.'
            },
            {
                id: 'corruption_partial',
                text: '경고만 준다',
                moneyChange: 0,
                popChange: 0,
                happinessChange: -5,
                outcomeDescription: '솜방망이 처벌...'
            }
        ]
    }
];

// ============================================
// 계절 이벤트 (Seasonal Events)
// ============================================

const SEASONAL_EVENTS: GameEvent[] = [
    // 봄 이벤트
    {
        id: 'spring_cherry_blossom',
        title: '벚꽃 축제',
        description: '연못가에 벚꽃이 만개했습니다. 축제를 열까요?',
        category: 'seasonal',
        season: Season.SPRING,
        weight: 25,
        options: [
            {
                id: 'cherry_festival',
                text: '성대한 축제를 연다',
                moneyChange: -100,
                popChange: 5,
                happinessChange: 20,
                outcomeDescription: '아름다운 벚꽃 아래 춤추는 오리들!'
            },
            {
                id: 'cherry_simple',
                text: '소박하게 즐긴다',
                moneyChange: -20,
                popChange: 0,
                happinessChange: 10,
                outcomeDescription: '조용하지만 아름다운 봄날.'
            }
        ]
    },
    {
        id: 'spring_migration_return',
        title: '철새의 귀환',
        description: '겨울을 보내고 돌아온 철새 친구들이 있습니다.',
        category: 'seasonal',
        season: Season.SPRING,
        weight: 20,
        options: [
            {
                id: 'migration_welcome',
                text: '환영회를 연다',
                moneyChange: -50,
                popChange: 0,
                happinessChange: 10,
                outcomeDescription: '먼 곳의 이야기를 들었습니다.',
                unlockCharacter: 'migrant_bird'
            },
            {
                id: 'migration_info',
                text: '정보를 교환한다',
                moneyChange: 30,
                popChange: 0,
                happinessChange: 5,
                outcomeDescription: '유용한 정보를 얻었습니다!'
            }
        ]
    },
    {
        id: 'spring_flood',
        title: '봄 홍수',
        description: '눈이 녹으면서 연못 수위가 급격히 올랐습니다!',
        category: 'seasonal',
        season: Season.SPRING,
        weight: 15,
        options: [
            {
                id: 'flood_evacuate',
                text: '높은 곳으로 대피',
                moneyChange: -80,
                popChange: 0,
                happinessChange: -5,
                outcomeDescription: '안전하게 대피했습니다.'
            },
            {
                id: 'flood_dam',
                text: '임시 댐을 쌓는다',
                moneyChange: -150,
                popChange: 0,
                happinessChange: 5,
                outcomeDescription: '오리들의 협동심! 위기 극복!'
            },
            {
                id: 'flood_swim',
                text: '오리니까 괜찮아',
                moneyChange: 0,
                popChange: -3,
                happinessChange: -10,
                outcomeDescription: '헤엄은 치지만... 둥지가 떠내려갔어요!'
            }
        ]
    },
    {
        id: 'spring_love_season',
        title: '짝짓기 시즌',
        description: '봄이 왔습니다. 오리들이 들떠있습니다.',
        category: 'seasonal',
        season: Season.SPRING,
        weight: 20,
        options: [
            {
                id: 'love_matchmaking',
                text: '중매를 서준다',
                moneyChange: -30,
                popChange: 8,
                happinessChange: 15,
                outcomeDescription: '새로운 커플들이 탄생했습니다!'
            },
            {
                id: 'love_natural',
                text: '자연에 맡긴다',
                moneyChange: 0,
                popChange: 4,
                happinessChange: 5,
                outcomeDescription: '자연스러운 만남...'
            }
        ]
    },

    // 여름 이벤트
    {
        id: 'summer_heatwave',
        title: '폭염',
        description: '너무 더워서 연못 물이 줄어들고 있습니다!',
        category: 'seasonal',
        season: Season.SUMMER,
        weight: 25,
        options: [
            {
                id: 'heat_shade',
                text: '그늘막을 설치한다',
                moneyChange: -100,
                popChange: 0,
                happinessChange: 10,
                outcomeDescription: '시원한 그늘! 오리들이 감사합니다.'
            },
            {
                id: 'heat_dig',
                text: '연못을 더 깊이 판다',
                moneyChange: -200,
                popChange: 0,
                happinessChange: 5,
                outcomeDescription: '시원한 깊은 물! 장기적인 해결책!'
            },
            {
                id: 'heat_endure',
                text: '참고 견딘다',
                moneyChange: 0,
                popChange: -5,
                happinessChange: -15,
                outcomeDescription: '많은 오리들이 고통받았습니다...'
            }
        ]
    },
    {
        id: 'summer_swimming_contest',
        title: '수영 대회',
        description: '여름 수영 대회를 개최하자는 의견이 있습니다.',
        category: 'seasonal',
        season: Season.SUMMER,
        weight: 20,
        options: [
            {
                id: 'swim_big',
                text: '대규모 대회 개최',
                moneyChange: -150,
                popChange: 0,
                happinessChange: 20,
                outcomeDescription: '역대급 수영 대회! 전설의 기록 탄생!'
            },
            {
                id: 'swim_local',
                text: '동네 대회로',
                moneyChange: -30,
                popChange: 0,
                happinessChange: 10,
                outcomeDescription: '화목한 동네 잔치!'
            }
        ]
    },
    {
        id: 'summer_drought',
        title: '가뭄',
        description: '비가 오지 않아 식량이 부족합니다.',
        category: 'seasonal',
        season: Season.SUMMER,
        weight: 15,
        options: [
            {
                id: 'drought_ration',
                text: '배급제 실시',
                moneyChange: 0,
                popChange: -2,
                happinessChange: -10,
                outcomeDescription: '힘든 시기... 하지만 공평하게.'
            },
            {
                id: 'drought_expedition',
                text: '물을 찾아 탐험대 파견',
                moneyChange: -100,
                popChange: 0,
                happinessChange: 0,
                outcomeDescription: '새로운 수원을 발견했습니다!',
                setFlag: 'found_new_water'
            },
            {
                id: 'drought_pray',
                text: '비를 위해 기도한다',
                moneyChange: -20,
                popChange: 0,
                happinessChange: 5,
                outcomeDescription: Math.random() > 0.7 ? '기적이 일어났습니다!' : '마음만은 편안해졌습니다.'
            }
        ]
    },
    {
        id: 'summer_night_festival',
        title: '한여름 밤의 축제',
        description: '별빛 아래 축제를 열자는 제안이 있습니다.',
        category: 'seasonal',
        season: Season.SUMMER,
        weight: 18,
        options: [
            {
                id: 'night_festival_yes',
                text: '불꽃놀이까지!',
                moneyChange: -200,
                popChange: 0,
                happinessChange: 25,
                outcomeDescription: '환상적인 밤! 평생 기억할 축제!'
            },
            {
                id: 'night_festival_simple',
                text: '조촐하게',
                moneyChange: -50,
                popChange: 0,
                happinessChange: 12,
                outcomeDescription: '별빛 아래 노래하는 오리들.'
            }
        ]
    },

    // 가을 이벤트
    {
        id: 'autumn_harvest',
        title: '수확의 계절',
        description: '풍성한 가을! 수확 축제를 열까요?',
        category: 'seasonal',
        season: Season.AUTUMN,
        weight: 25,
        options: [
            {
                id: 'harvest_feast',
                text: '수확 대잔치',
                moneyChange: 100,
                popChange: 0,
                happinessChange: 20,
                outcomeDescription: '배부르고 행복한 오리 왕국!'
            },
            {
                id: 'harvest_save',
                text: '겨울을 대비해 저장',
                moneyChange: 200,
                popChange: 0,
                happinessChange: 5,
                outcomeDescription: '현명한 선택! 겨울이 두렵지 않습니다.'
            },
            {
                id: 'harvest_share',
                text: '이웃과 나눈다',
                moneyChange: -50,
                popChange: 5,
                happinessChange: 15,
                outcomeDescription: '나눔의 기쁨! 새 친구들이 왔습니다.'
            }
        ]
    },
    {
        id: 'autumn_migration_goodbye',
        title: '철새의 이별',
        description: '철새 친구들이 따뜻한 곳으로 떠납니다.',
        category: 'seasonal',
        season: Season.AUTUMN,
        weight: 20,
        requiresCharacter: 'migrant_bird',
        options: [
            {
                id: 'goodbye_party',
                text: '송별회를 연다',
                moneyChange: -80,
                popChange: 0,
                happinessChange: 10,
                outcomeDescription: '다음 봄에 다시 만나요!',
                triggersChain: 'sky_migration'
            },
            {
                id: 'goodbye_join',
                text: '함께 가고 싶다고 말한다',
                moneyChange: 0,
                popChange: 0,
                happinessChange: 0,
                outcomeDescription: '철새가 흥미로운 제안을 합니다...',
                triggersChain: 'sky_migration',
                setFlag: 'wants_to_migrate'
            }
        ]
    },
    {
        id: 'autumn_fallen_leaves',
        title: '낙엽',
        description: '연못에 낙엽이 가득합니다.',
        category: 'seasonal',
        season: Season.AUTUMN,
        weight: 15,
        options: [
            {
                id: 'leaves_clean',
                text: '청소한다',
                moneyChange: -30,
                popChange: 0,
                happinessChange: 5,
                outcomeDescription: '깨끗한 연못!'
            },
            {
                id: 'leaves_play',
                text: '낙엽놀이를 한다',
                moneyChange: 0,
                popChange: 0,
                happinessChange: 10,
                outcomeDescription: '바스락바스락! 재미있어!'
            },
            {
                id: 'leaves_compost',
                text: '퇴비로 만든다',
                moneyChange: 50,
                popChange: 0,
                happinessChange: 0,
                outcomeDescription: '실용적인 선택!'
            }
        ]
    },
    {
        id: 'autumn_moon_festival',
        title: '보름달 축제',
        description: '크고 둥근 보름달이 떴습니다.',
        category: 'seasonal',
        season: Season.AUTUMN,
        weight: 18,
        options: [
            {
                id: 'moon_ceremony',
                text: '달맞이 의식을 한다',
                moneyChange: -50,
                popChange: 0,
                happinessChange: 15,
                outcomeDescription: '신비로운 밤... 행운이 찾아올 것 같습니다.',
                setFlag: 'moon_blessing'
            },
            {
                id: 'moon_sleep',
                text: '그냥 잔다',
                moneyChange: 0,
                popChange: 0,
                happinessChange: 0,
                outcomeDescription: '편안한 밤이었습니다.'
            }
        ]
    },

    // 겨울 이벤트
    {
        id: 'winter_first_snow',
        title: '첫눈',
        description: '올해 첫눈이 내렸습니다!',
        category: 'seasonal',
        season: Season.WINTER,
        weight: 20,
        options: [
            {
                id: 'snow_play',
                text: '눈싸움을 한다',
                moneyChange: 0,
                popChange: 0,
                happinessChange: 15,
                outcomeDescription: '꽥꽥! 차갑지만 재미있어!'
            },
            {
                id: 'snow_prepare',
                text: '동파 대비를 한다',
                moneyChange: -50,
                popChange: 0,
                happinessChange: 0,
                outcomeDescription: '현명한 선택! 안전한 겨울!'
            }
        ]
    },
    {
        id: 'winter_frozen_pond',
        title: '얼어붙은 연못',
        description: '연못이 꽁꽁 얼었습니다!',
        category: 'seasonal',
        season: Season.WINTER,
        weight: 20,
        options: [
            {
                id: 'ice_skating',
                text: '스케이팅을 즐긴다',
                moneyChange: -20,
                popChange: 0,
                happinessChange: 15,
                outcomeDescription: '미끄러지는 재미! (넘어지기도...)'
            },
            {
                id: 'ice_break',
                text: '얼음을 깬다',
                moneyChange: -50,
                popChange: 0,
                happinessChange: 0,
                outcomeDescription: '물에 접근할 수 있게 되었습니다.'
            },
            {
                id: 'ice_fishing',
                text: '얼음낚시를 한다',
                moneyChange: 80,
                popChange: 0,
                happinessChange: 10,
                outcomeDescription: '신선한 물고기 확보!'
            }
        ]
    },
    {
        id: 'winter_cold_wave',
        title: '한파',
        description: '매서운 추위가 몰아칩니다!',
        category: 'seasonal',
        season: Season.WINTER,
        weight: 15,
        options: [
            {
                id: 'cold_huddle',
                text: '모두 모여 체온을 나눈다',
                moneyChange: 0,
                popChange: 0,
                happinessChange: 10,
                outcomeDescription: '따뜻한 우정!'
            },
            {
                id: 'cold_fire',
                text: '불을 피운다',
                moneyChange: -100,
                popChange: 0,
                happinessChange: 15,
                outcomeDescription: '따뜻한 불 주위에 모인 오리들.'
            },
            {
                id: 'cold_endure',
                text: '각자 버틴다',
                moneyChange: 0,
                popChange: -5,
                happinessChange: -20,
                outcomeDescription: '힘든 겨울... 일부가 얼었습니다.'
            }
        ]
    },
    {
        id: 'winter_new_year',
        title: '새해',
        description: '새해가 밝았습니다!',
        category: 'seasonal',
        season: Season.WINTER,
        weight: 25,
        oneTime: false,
        options: [
            {
                id: 'newyear_celebration',
                text: '성대한 새해 축하',
                moneyChange: -200,
                popChange: 0,
                happinessChange: 25,
                outcomeDescription: '새해 복 많이 받으세요! 꽥!'
            },
            {
                id: 'newyear_resolutions',
                text: '새해 목표를 세운다',
                moneyChange: 0,
                popChange: 0,
                happinessChange: 10,
                outcomeDescription: '올해는 더 나은 왕국을 만들겠습니다!'
            },
            {
                id: 'newyear_quiet',
                text: '조용히 보낸다',
                moneyChange: 0,
                popChange: 0,
                happinessChange: 0,
                outcomeDescription: '차분한 새해 아침.'
            }
        ]
    },
    {
        id: 'winter_sick_duck',
        title: '겨울 감기',
        description: '많은 오리들이 감기에 걸렸습니다.',
        category: 'seasonal',
        season: Season.WINTER,
        weight: 15,
        options: [
            {
                id: 'sick_medicine',
                text: '약초를 구해온다',
                moneyChange: -80,
                popChange: 0,
                happinessChange: 5,
                outcomeDescription: '따뜻한 수프와 약초로 회복!'
            },
            {
                id: 'sick_hospital',
                text: '병원을 짓는다',
                moneyChange: -300,
                popChange: 0,
                happinessChange: 10,
                outcomeDescription: '앞으로 이런 일에 대비할 수 있습니다!',
                unlockBuilding: BuildingType.HOSPITAL
            },
            {
                id: 'sick_rest',
                text: '푹 쉬게 한다',
                moneyChange: -30,
                popChange: -2,
                happinessChange: -5,
                outcomeDescription: '시간이 좀 걸렸지만 회복했습니다.'
            }
        ]
    }
];

// ============================================
// 위기 이벤트 (Crisis Events)
// ============================================

const CRISIS_EVENTS: GameEvent[] = [
    {
        id: 'crisis_fox_scout',
        title: '여우 정찰병!',
        description: '연못 근처에서 여우가 목격되었습니다!',
        category: 'crisis',
        weight: 15,
        options: [
            {
                id: 'fox_hide',
                text: '모두 숨는다',
                moneyChange: 0,
                popChange: 0,
                happinessChange: -10,
                outcomeDescription: '무사히 지나갔지만... 불안합니다.'
            },
            {
                id: 'fox_confront',
                text: '맞서 싸운다',
                moneyChange: 0,
                popChange: -3,
                happinessChange: 5,
                outcomeDescription: '여우를 쫓아냈지만 희생이 있었습니다.',
                setFlag: 'fought_fox'
            },
            {
                id: 'fox_negotiate',
                text: '협상을 시도한다',
                moneyChange: -50,
                popChange: 0,
                happinessChange: 0,
                outcomeDescription: '뇌물로 일단 물러갔습니다...',
                triggersChain: 'fox_invasion'
            }
        ]
    },
    {
        id: 'crisis_fox_invasion_start',
        title: '여우 군단의 등장',
        description: '여우들이 대규모로 나타났습니다! 전쟁이 시작됩니다!',
        category: 'crisis',
        chainId: 'fox_invasion',
        chainStep: 0,
        weight: 0,  // 체인으로만 발생
        options: [
            {
                id: 'invasion_war',
                text: '전면전을 선포한다',
                moneyChange: -200,
                popChange: -10,
                happinessChange: -10,
                outcomeDescription: '전쟁의 시작! 모두가 무장합니다.',
                continuesChain: 'fox_invasion',
                setFlag: 'chose_war'
            },
            {
                id: 'invasion_diplomacy',
                text: '협상을 제안한다',
                moneyChange: -100,
                popChange: 0,
                happinessChange: -5,
                outcomeDescription: '여우 대장이 협상에 응했습니다.',
                continuesChain: 'fox_invasion',
                setFlag: 'chose_diplomacy',
                unlockCharacter: 'fox_general'
            }
        ]
    },
    {
        id: 'crisis_plague',
        title: '전염병',
        description: '알 수 없는 병이 퍼지고 있습니다!',
        category: 'crisis',
        weight: 8,
        minEra: Era.VILLAGE,
        options: [
            {
                id: 'plague_quarantine',
                text: '격리 조치',
                moneyChange: -100,
                popChange: -5,
                happinessChange: -15,
                outcomeDescription: '힘든 시기를 버텼습니다.'
            },
            {
                id: 'plague_medicine',
                text: '치료제를 개발한다',
                moneyChange: -300,
                popChange: -3,
                happinessChange: 5,
                outcomeDescription: '기적의 치료제! 영웅이 탄생했습니다.',
                setFlag: 'developed_medicine'
            },
            {
                id: 'plague_pray',
                text: '신전에서 기도한다',
                moneyChange: -50,
                popChange: -8,
                happinessChange: 0,
                outcomeDescription: Math.random() > 0.5
                    ? '기적이 일어났습니다!'
                    : '많은 희생이 있었습니다...',
                requiresBuilding: BuildingType.TEMPLE
            }
        ]
    },
    {
        id: 'crisis_flood_disaster',
        title: '대홍수',
        description: '기록적인 폭우로 연못이 범람했습니다!',
        category: 'crisis',
        weight: 10,
        season: Season.SUMMER,
        options: [
            {
                id: 'flood_evacuate_all',
                text: '전원 대피',
                moneyChange: -150,
                popChange: -2,
                happinessChange: -5,
                outcomeDescription: '대부분 무사히 대피했습니다.'
            },
            {
                id: 'flood_save_treasure',
                text: '보물도 챙긴다',
                moneyChange: 100,
                popChange: -8,
                happinessChange: -20,
                outcomeDescription: '재산은 지켰지만 많은 희생이...'
            },
            {
                id: 'flood_heroes',
                text: '구조대를 조직한다',
                moneyChange: -200,
                popChange: 0,
                happinessChange: 10,
                outcomeDescription: '영웅적인 구조! 모두가 감동했습니다.',
                setFlag: 'flood_heroes'
            }
        ]
    },
    {
        id: 'crisis_famine',
        title: '기근',
        description: '식량이 바닥났습니다! 오리들이 굶주리고 있습니다!',
        category: 'crisis',
        weight: 10,
        maxHappiness: 40,
        options: [
            {
                id: 'famine_ration',
                text: '엄격한 배급제',
                moneyChange: 0,
                popChange: -5,
                happinessChange: -15,
                outcomeDescription: '힘든 시기... 많은 이가 떠났습니다.'
            },
            {
                id: 'famine_hunt',
                text: '식량 원정대 파견',
                moneyChange: -100,
                popChange: -3,
                happinessChange: -5,
                outcomeDescription: '새로운 식량원을 찾았습니다!',
                setFlag: 'found_food_source'
            },
            {
                id: 'famine_trade',
                text: '다른 왕국에 도움 요청',
                moneyChange: -200,
                popChange: 0,
                happinessChange: 0,
                outcomeDescription: '이웃 왕국이 식량을 보내줬습니다.',
                setFlag: 'foreign_aid'
            }
        ]
    },
    {
        id: 'crisis_predator_eagle',
        title: '독수리의 습격!',
        description: '하늘에서 독수리가 내려옵니다!',
        category: 'crisis',
        weight: 12,
        options: [
            {
                id: 'eagle_scatter',
                text: '흩어진다',
                moneyChange: 0,
                popChange: -5,
                happinessChange: -15,
                outcomeDescription: '혼란 속에 많은 희생이...'
            },
            {
                id: 'eagle_shelter',
                text: '대피소로 숨는다',
                moneyChange: 0,
                popChange: -1,
                happinessChange: -5,
                outcomeDescription: '대부분 안전하게 대피했습니다.'
            },
            {
                id: 'eagle_fight',
                text: '거위 경비대 출동!',
                moneyChange: -100,
                popChange: 0,
                happinessChange: 10,
                outcomeDescription: '용감한 거위들이 독수리를 쫓아냈습니다!',
                requiresBuilding: BuildingType.BARRACKS,
                setFlag: 'defeated_eagle'
            }
        ]
    },
    {
        id: 'crisis_rebellion',
        title: '반란의 조짐',
        description: '일부 오리들이 불만을 품고 반란을 계획합니다!',
        category: 'crisis',
        weight: 8,
        maxHappiness: 30,
        minPopulation: 50,
        options: [
            {
                id: 'rebellion_suppress',
                text: '무력 진압',
                moneyChange: -50,
                popChange: -10,
                happinessChange: -20,
                outcomeDescription: '반란은 진압됐지만 상처가 남았습니다.'
            },
            {
                id: 'rebellion_negotiate',
                text: '대화로 해결',
                moneyChange: -100,
                popChange: 0,
                happinessChange: 10,
                outcomeDescription: '요구사항을 들어주고 화해했습니다.',
                setFlag: 'peaceful_resolution'
            },
            {
                id: 'rebellion_reform',
                text: '개혁을 약속',
                moneyChange: 0,
                popChange: 0,
                happinessChange: 5,
                outcomeDescription: '변화를 약속했습니다. 실천해야겠죠.',
                triggersChain: 'rebel_uprising',
                unlockCharacter: 'rebel_sparrow'
            }
        ]
    },
    {
        id: 'crisis_fire',
        title: '화재!',
        description: '둥지에서 불이 났습니다!',
        category: 'crisis',
        weight: 10,
        minEra: Era.VILLAGE,
        options: [
            {
                id: 'fire_water',
                text: '물로 끈다',
                moneyChange: -50,
                popChange: -2,
                happinessChange: -10,
                outcomeDescription: '불을 껐지만 피해가 있었습니다.'
            },
            {
                id: 'fire_sacrifice',
                text: '건물을 포기하고 대피',
                moneyChange: -200,
                popChange: 0,
                happinessChange: -5,
                outcomeDescription: '건물은 탔지만 모두 무사합니다.'
            },
            {
                id: 'fire_heroes',
                text: '소방대 결성',
                moneyChange: -100,
                popChange: 0,
                happinessChange: 5,
                outcomeDescription: '용감한 소방 오리들! 영웅 탄생!',
                setFlag: 'fire_brigade'
            }
        ]
    },
    {
        id: 'crisis_earthquake',
        title: '지진!',
        description: '땅이 흔들립니다! 둥지들이 무너지고 있어요!',
        category: 'crisis',
        weight: 5,
        minEra: Era.CITY,
        options: [
            {
                id: 'quake_duck_cover',
                text: '엎드려! 머리 보호!',
                moneyChange: -100,
                popChange: -5,
                happinessChange: -15,
                outcomeDescription: '피해가 있었지만 최소화했습니다.'
            },
            {
                id: 'quake_evacuate',
                text: '넓은 곳으로 대피',
                moneyChange: -50,
                popChange: -3,
                happinessChange: -10,
                outcomeDescription: '신속한 대피로 피해를 줄였습니다.'
            },
            {
                id: 'quake_rebuild',
                text: '재건을 선포한다',
                moneyChange: -300,
                popChange: 0,
                happinessChange: 5,
                outcomeDescription: '더 강한 왕국을 다시 세우겠습니다!',
                setFlag: 'earthquake_survivor'
            }
        ]
    }
];

// ============================================
// 기회 이벤트 (Opportunity Events)
// ============================================

const OPPORTUNITY_EVENTS: GameEvent[] = [
    {
        id: 'opportunity_golden_egg',
        title: '황금알 발견!',
        description: '연못에서 반짝이는 황금알을 발견했습니다!',
        category: 'opportunity',
        weight: 5,
        options: [
            {
                id: 'golden_sell',
                text: '팔아서 부자가 된다',
                moneyChange: 500,
                popChange: 0,
                happinessChange: 10,
                outcomeDescription: '부자가 됐습니다! 하지만 알이 뭐였을까...'
            },
            {
                id: 'golden_incubate',
                text: '부화시킨다',
                moneyChange: 0,
                popChange: 0,
                happinessChange: 0,
                outcomeDescription: '무엇이 태어날까요...?',
                triggersChain: 'mysterious_egg',
                setFlag: 'has_golden_egg'
            },
            {
                id: 'golden_shrine',
                text: '신전에 봉헌한다',
                moneyChange: 0,
                popChange: 0,
                happinessChange: 20,
                outcomeDescription: '경건한 마음에 축복이 내릴 것입니다.',
                requiresBuilding: BuildingType.TEMPLE,
                setFlag: 'blessed_by_egg'
            }
        ]
    },
    {
        id: 'opportunity_merchant_visit',
        title: '거위 상인의 방문',
        description: '유명한 거위 상인이 왕국을 방문했습니다.',
        category: 'opportunity',
        weight: 15,
        minEra: Era.VILLAGE,
        options: [
            {
                id: 'merchant_trade',
                text: '거래한다',
                moneyChange: 100,
                popChange: 0,
                happinessChange: 5,
                outcomeDescription: '좋은 거래였습니다!',
                unlockCharacter: 'merchant_goose'
            },
            {
                id: 'merchant_partner',
                text: '파트너십 제안',
                moneyChange: -200,
                popChange: 0,
                happinessChange: 0,
                outcomeDescription: '장기적인 무역 관계가 시작됩니다.',
                triggersChain: 'merchant_empire',
                unlockCharacter: 'merchant_goose'
            },
            {
                id: 'merchant_reject',
                text: '우리끼리 산다',
                moneyChange: 0,
                popChange: 0,
                happinessChange: 0,
                outcomeDescription: '외부인은 믿을 수 없어요.'
            }
        ]
    },
    {
        id: 'opportunity_treasure_map',
        title: '보물 지도',
        description: '오래된 보물 지도를 발견했습니다!',
        category: 'opportunity',
        weight: 10,
        options: [
            {
                id: 'treasure_expedition',
                text: '탐험대를 보낸다',
                moneyChange: -100,
                popChange: -2,
                happinessChange: 0,
                outcomeDescription: Math.random() > 0.3
                    ? '보물을 찾았습니다! +300 오링!'
                    : '빈손으로 돌아왔습니다...',
                setFlag: 'sent_expedition'
            },
            {
                id: 'treasure_sell',
                text: '지도를 판다',
                moneyChange: 50,
                popChange: 0,
                happinessChange: 0,
                outcomeDescription: '안전하게 돈을 벌었습니다.'
            },
            {
                id: 'treasure_later',
                text: '나중을 위해 보관',
                moneyChange: 0,
                popChange: 0,
                happinessChange: 0,
                outcomeDescription: '언젠가 떠날 그날을 위해...',
                setFlag: 'has_treasure_map'
            }
        ]
    },
    {
        id: 'opportunity_wise_turtle',
        title: '지혜로운 거북',
        description: '아주 오래된 거북이 연못에 나타났습니다.',
        category: 'opportunity',
        weight: 8,
        options: [
            {
                id: 'turtle_wisdom',
                text: '지혜를 구한다',
                moneyChange: 0,
                popChange: 0,
                happinessChange: 10,
                outcomeDescription: '고대의 지혜를 배웠습니다.',
                triggersChain: 'ancient_secret',
                unlockCharacter: 'ancient_turtle'
            },
            {
                id: 'turtle_ignore',
                text: '바쁘다고 한다',
                moneyChange: 0,
                popChange: 0,
                happinessChange: 0,
                outcomeDescription: '거북은 조용히 떠났습니다.'
            }
        ]
    },
    {
        id: 'opportunity_swan_visit',
        title: '신비한 백조',
        description: '아름답고 신비로운 백조가 나타났습니다.',
        category: 'opportunity',
        weight: 5,
        minEra: Era.CITY,
        options: [
            {
                id: 'swan_welcome',
                text: '극진히 대접한다',
                moneyChange: -150,
                popChange: 0,
                happinessChange: 15,
                outcomeDescription: '백조가 축복을 내렸습니다!',
                unlockCharacter: 'mystic_swan',
                setFlag: 'blessed_by_swan'
            },
            {
                id: 'swan_question',
                text: '미래를 묻는다',
                moneyChange: -50,
                popChange: 0,
                happinessChange: 5,
                outcomeDescription: '수수께끼 같은 예언을 들었습니다...',
                unlockCharacter: 'mystic_swan',
                triggersChain: 'true_path'
            }
        ]
    },
    {
        id: 'opportunity_inventor',
        title: '발명가 오리',
        description: '어떤 오리가 신기한 발명품을 들고 왔습니다.',
        category: 'opportunity',
        weight: 12,
        minEra: Era.VILLAGE,
        options: [
            {
                id: 'inventor_invest',
                text: '투자한다',
                moneyChange: -200,
                popChange: 0,
                happinessChange: 5,
                outcomeDescription: Math.random() > 0.4
                    ? '대박! 혁신적인 발명품! +500 오링!'
                    : '음... 실패작이네요...',
                setFlag: 'invested_invention'
            },
            {
                id: 'inventor_reject',
                text: '위험하다며 거절',
                moneyChange: 0,
                popChange: 0,
                happinessChange: -5,
                outcomeDescription: '발명가가 다른 연못으로 떠났습니다.'
            }
        ]
    },
    {
        id: 'opportunity_celebrity_duck',
        title: '유명 오리의 방문',
        description: '전설적인 모험가 오리가 왕국을 방문했습니다!',
        category: 'opportunity',
        weight: 8,
        minEra: Era.CITY,
        options: [
            {
                id: 'celebrity_tour',
                text: '왕국 투어를 시켜준다',
                moneyChange: -50,
                popChange: 5,
                happinessChange: 15,
                outcomeDescription: '왕국의 명성이 높아졌습니다!'
            },
            {
                id: 'celebrity_story',
                text: '모험담을 듣는다',
                moneyChange: 0,
                popChange: 0,
                happinessChange: 10,
                outcomeDescription: '놀라운 이야기들! 젊은 오리들이 영감을 받았습니다.',
                triggersChain: 'sea_expedition'
            }
        ]
    },
    {
        id: 'opportunity_rainbow',
        title: '쌍무지개',
        description: '하늘에 아름다운 쌍무지개가 떴습니다!',
        category: 'opportunity',
        weight: 10,
        options: [
            {
                id: 'rainbow_celebration',
                text: '축복의 징조로 축하',
                moneyChange: -30,
                popChange: 0,
                happinessChange: 15,
                outcomeDescription: '행운이 찾아올 것 같습니다!',
                setFlag: 'rainbow_blessing'
            },
            {
                id: 'rainbow_photo',
                text: '기념 그림을 그린다',
                moneyChange: -10,
                popChange: 0,
                happinessChange: 10,
                outcomeDescription: '아름다운 기념품이 생겼습니다!'
            }
        ]
    },
    {
        id: 'opportunity_peacock_embassy',
        title: '공작 왕국의 사절단',
        description: '화려한 공작 왕국에서 사절단이 왔습니다.',
        category: 'opportunity',
        weight: 8,
        minEra: Era.KINGDOM,
        options: [
            {
                id: 'peacock_alliance',
                text: '동맹을 제안한다',
                moneyChange: -500,
                popChange: 0,
                happinessChange: 10,
                outcomeDescription: '강력한 동맹이 체결되었습니다!',
                unlockCharacter: 'royal_peacock',
                setFlag: 'peacock_alliance'
            },
            {
                id: 'peacock_trade',
                text: '무역 협정만',
                moneyChange: 200,
                popChange: 0,
                happinessChange: 5,
                outcomeDescription: '경제적 파트너십이 시작되었습니다.',
                unlockCharacter: 'royal_peacock'
            },
            {
                id: 'peacock_marriage',
                text: '결혼 동맹을 제안',
                moneyChange: -300,
                popChange: 0,
                happinessChange: 0,
                outcomeDescription: '흥미로운 제안입니다...',
                triggersChain: 'royal_wedding',
                unlockCharacter: 'royal_peacock'
            }
        ]
    },
    {
        id: 'opportunity_pirate_pelican',
        title: '해적 펠리컨',
        description: '바다에서 온 펠리컨 해적이 연못에 정박했습니다.',
        category: 'opportunity',
        weight: 6,
        minEra: Era.CITY,
        options: [
            {
                id: 'pirate_deal',
                text: '거래한다 (위험하지만...)',
                moneyChange: 200,
                popChange: 0,
                happinessChange: -5,
                outcomeDescription: '검은 거래... 하지만 이득이 있었습니다.',
                unlockCharacter: 'pirate_pelican'
            },
            {
                id: 'pirate_recruit',
                text: '왕국에 합류 제안',
                moneyChange: -100,
                popChange: 3,
                happinessChange: 0,
                outcomeDescription: '해적이 아군이 되었습니다!',
                unlockCharacter: 'pirate_pelican',
                triggersChain: 'sea_expedition'
            },
            {
                id: 'pirate_chase',
                text: '쫓아낸다',
                moneyChange: 0,
                popChange: 0,
                happinessChange: 5,
                outcomeDescription: '해적을 물리쳤습니다!'
            }
        ]
    }
];

// ============================================
// 캐릭터 이벤트 (Character Events)
// ============================================

const CHARACTER_EVENTS: GameEvent[] = [
    // 현자 오리 이벤트
    {
        id: 'char_sage_appear',
        title: '현자 오리의 출현',
        description: '연못 가장 깊은 곳에서 아주 오래된 오리가 나타났습니다.',
        category: 'character',
        weight: 5,
        minEra: Era.VILLAGE,
        options: [
            {
                id: 'sage_listen',
                text: '가르침을 청한다',
                moneyChange: 0,
                popChange: 0,
                happinessChange: 10,
                outcomeDescription: '"진정한 왕은 백성을 섬기는 자니라..."',
                unlockCharacter: 'sage_duck',
                triggersChain: 'sage_teachings'
            },
            {
                id: 'sage_gift',
                text: '선물을 드린다',
                moneyChange: -100,
                popChange: 0,
                happinessChange: 5,
                outcomeDescription: '현자가 미소 지었습니다.',
                unlockCharacter: 'sage_duck'
            }
        ]
    },
    {
        id: 'char_sage_advice',
        title: '현자의 조언',
        description: '현자 오리가 중요한 조언을 하려 합니다.',
        category: 'character',
        requiresCharacter: 'sage_duck',
        weight: 15,
        options: [
            {
                id: 'sage_advice_accept',
                text: '조언을 따른다',
                moneyChange: 0,
                popChange: 0,
                happinessChange: 10,
                outcomeDescription: '지혜로운 결정이었습니다!'
            },
            {
                id: 'sage_advice_reject',
                text: '내 방식대로 한다',
                moneyChange: 50,
                popChange: 0,
                happinessChange: -5,
                outcomeDescription: '현자가 고개를 젓습니다.'
            }
        ]
    },

    // 여우 대장 이벤트
    {
        id: 'char_fox_meeting',
        title: '여우 대장과의 회담',
        description: '여우 대장이 비밀 회담을 요청합니다.',
        category: 'character',
        requiresCharacter: 'fox_general',
        weight: 12,
        options: [
            {
                id: 'fox_meet_accept',
                text: '회담에 응한다',
                moneyChange: 0,
                popChange: 0,
                happinessChange: -5,
                outcomeDescription: '긴장된 회담... 하지만 평화의 가능성이?',
                continuesChain: 'fox_invasion'
            },
            {
                id: 'fox_meet_trap',
                text: '함정을 설치한다',
                moneyChange: -100,
                popChange: 0,
                happinessChange: 0,
                outcomeDescription: '위험한 도박입니다...',
                setFlag: 'fox_trap'
            }
        ]
    },

    // 그림자 까마귀 이벤트
    {
        id: 'char_crow_appear',
        title: '그림자 속의 목소리',
        description: '어둠 속에서 까마귀의 목소리가 들립니다.',
        category: 'character',
        weight: 6,
        minEra: Era.CITY,
        options: [
            {
                id: 'crow_listen',
                text: '들어본다',
                moneyChange: 0,
                popChange: 0,
                happinessChange: -5,
                outcomeDescription: '비밀스러운 정보를 얻었습니다...',
                unlockCharacter: 'shadow_crow',
                triggersChain: 'shadow_conspiracy'
            },
            {
                id: 'crow_ignore',
                text: '무시한다',
                moneyChange: 0,
                popChange: 0,
                happinessChange: 0,
                outcomeDescription: '까마귀가 사라졌습니다.'
            }
        ]
    },
    {
        id: 'char_crow_secret',
        title: '까마귀의 비밀 제안',
        description: '그림자 까마귀가 은밀한 제안을 합니다.',
        category: 'character',
        requiresCharacter: 'shadow_crow',
        weight: 10,
        options: [
            {
                id: 'crow_deal',
                text: '거래한다',
                moneyChange: 200,
                popChange: 0,
                happinessChange: -10,
                outcomeDescription: '어둠의 거래... 득인지 실인지...',
                setFlag: 'dark_deal'
            },
            {
                id: 'crow_refuse',
                text: '거절한다',
                moneyChange: 0,
                popChange: 0,
                happinessChange: 5,
                outcomeDescription: '깨끗한 양심!'
            }
        ]
    }
];

// ============================================
// 마일스톤 이벤트 (Milestone Events)
// ============================================

const MILESTONE_EVENTS: GameEvent[] = [
    {
        id: 'milestone_first_building',
        title: '첫 번째 건물!',
        description: '왕국에 첫 번째 건물이 세워졌습니다!',
        category: 'milestone',
        weight: 100,
        oneTime: true,
        options: [
            {
                id: 'first_building_celebrate',
                text: '축하한다',
                moneyChange: 0,
                popChange: 2,
                happinessChange: 10,
                outcomeDescription: '발전의 첫걸음!'
            }
        ]
    },
    {
        id: 'milestone_50_pop',
        title: '인구 50명 돌파!',
        description: '왕국의 인구가 50명을 넘었습니다!',
        category: 'milestone',
        minPopulation: 50,
        weight: 100,
        oneTime: true,
        options: [
            {
                id: '50_pop_ceremony',
                text: '시대의 변화를 선포',
                moneyChange: -100,
                popChange: 5,
                happinessChange: 15,
                outcomeDescription: '이제 마을입니다!'
            }
        ]
    },
    {
        id: 'milestone_200_pop',
        title: '인구 200명 돌파!',
        description: '왕국의 인구가 200명을 넘었습니다!',
        category: 'milestone',
        minPopulation: 200,
        weight: 100,
        oneTime: true,
        options: [
            {
                id: '200_pop_city',
                text: '도시 승격',
                moneyChange: -500,
                popChange: 10,
                happinessChange: 20,
                outcomeDescription: '도시의 탄생!'
            }
        ]
    },
    {
        id: 'milestone_500_pop',
        title: '인구 500명 돌파!',
        description: '진정한 왕국이 되었습니다!',
        category: 'milestone',
        minPopulation: 500,
        weight: 100,
        oneTime: true,
        options: [
            {
                id: '500_pop_kingdom',
                text: '대관식',
                moneyChange: -1000,
                popChange: 20,
                happinessChange: 30,
                outcomeDescription: '오링왕국의 탄생!'
            }
        ]
    },
    {
        id: 'milestone_1000_pop',
        title: '인구 1000명 돌파!',
        description: '제국의 규모에 도달했습니다!',
        category: 'milestone',
        minPopulation: 1000,
        weight: 100,
        oneTime: true,
        options: [
            {
                id: '1000_pop_empire',
                text: '제국 선포',
                moneyChange: -5000,
                popChange: 50,
                happinessChange: 40,
                outcomeDescription: '오링 제국의 탄생!'
            }
        ]
    },
    {
        id: 'milestone_millionaire',
        title: '백만장자!',
        description: '오링이 1,000,000개를 넘었습니다!',
        category: 'milestone',
        minMoney: 1000000,
        weight: 100,
        oneTime: true,
        options: [
            {
                id: 'millionaire_celebrate',
                text: '부자 파티',
                moneyChange: -10000,
                popChange: 0,
                happinessChange: 30,
                outcomeDescription: '황금 연못의 전설!'
            },
            {
                id: 'millionaire_share',
                text: '백성들과 나눈다',
                moneyChange: -100000,
                popChange: 50,
                happinessChange: 50,
                outcomeDescription: '덕이 높은 왕!'
            }
        ]
    },
    {
        id: 'milestone_100_happiness',
        title: '완벽한 행복!',
        description: '행복도가 100%에 도달했습니다!',
        category: 'milestone',
        minHappiness: 100,
        weight: 100,
        oneTime: true,
        options: [
            {
                id: 'perfect_happiness',
                text: '이 순간을 기억한다',
                moneyChange: 0,
                popChange: 10,
                happinessChange: 0,
                outcomeDescription: '유토피아!',
                setFlag: 'achieved_perfect_happiness'
            }
        ]
    }
];

// ============================================
// 체인 이벤트 상세 (Chain Events Detail)
// ============================================

const CHAIN_EVENTS: GameEvent[] = [
    // ========== 여우 침공 체인 ==========
    {
        id: 'fox_chain_1_war',
        title: '첫 번째 전투',
        description: '여우 군단과의 첫 전투가 벌어집니다!',
        category: 'chain',
        chainId: 'fox_invasion',
        chainStep: 1,
        requiresFlag: 'chose_war',
        options: [
            {
                id: 'fox_war_attack',
                text: '전면 공격',
                moneyChange: -300,
                popChange: -15,
                happinessChange: -10,
                outcomeDescription: '치열한 전투! 승리했지만 큰 희생이...',
                continuesChain: 'fox_invasion',
                setFlag: 'won_first_battle'
            },
            {
                id: 'fox_war_defend',
                text: '방어에 집중',
                moneyChange: -150,
                popChange: -5,
                happinessChange: -5,
                outcomeDescription: '성벽을 지켰습니다!',
                continuesChain: 'fox_invasion'
            }
        ]
    },
    {
        id: 'fox_chain_1_diplomacy',
        title: '협상 테이블',
        description: '여우 대장과 마주 앉았습니다.',
        category: 'chain',
        chainId: 'fox_invasion',
        chainStep: 1,
        requiresFlag: 'chose_diplomacy',
        options: [
            {
                id: 'fox_diplo_tribute',
                text: '조공을 바친다',
                moneyChange: -500,
                popChange: 0,
                happinessChange: -15,
                outcomeDescription: '굴욕적이지만 평화를 샀습니다.',
                continuesChain: 'fox_invasion',
                setFlag: 'paid_tribute'
            },
            {
                id: 'fox_diplo_deal',
                text: '무역 협정 제안',
                moneyChange: -200,
                popChange: 0,
                happinessChange: -5,
                outcomeDescription: '여우 대장이 흥미를 보입니다...',
                continuesChain: 'fox_invasion',
                setFlag: 'trade_negotiation'
            }
        ]
    },
    {
        id: 'fox_chain_2',
        title: '전쟁의 한복판',
        description: '전쟁이 격화됩니다.',
        category: 'chain',
        chainId: 'fox_invasion',
        chainStep: 2,
        options: [
            {
                id: 'fox_chain_2_push',
                text: '밀어붙인다',
                moneyChange: -400,
                popChange: -20,
                happinessChange: -15,
                outcomeDescription: '피의 대가를 치르고 있습니다...',
                continuesChain: 'fox_invasion'
            },
            {
                id: 'fox_chain_2_truce',
                text: '휴전 제안',
                moneyChange: -100,
                popChange: 0,
                happinessChange: 5,
                outcomeDescription: '잠시 숨을 돌립니다.',
                continuesChain: 'fox_invasion',
                setFlag: 'proposed_truce'
            }
        ]
    },
    {
        id: 'fox_chain_final_war',
        title: '최후의 결전',
        description: '모든 것을 건 마지막 전투!',
        category: 'chain',
        chainId: 'fox_invasion',
        chainStep: 7,
        requiresFlag: 'chose_war',
        options: [
            {
                id: 'fox_final_victory',
                text: '결사항전!',
                moneyChange: -1000,
                popChange: -30,
                happinessChange: 20,
                outcomeDescription: '승리! 여우를 몰아냈습니다!',
                endsChain: true,
                setFlag: 'defeated_fox_army'
            },
            {
                id: 'fox_final_surrender',
                text: '항복한다',
                moneyChange: 0,
                popChange: 0,
                happinessChange: -30,
                outcomeDescription: '왕국이 여우에게 넘어갔습니다...',
                endsChain: true,
                setFlag: 'fox_victory',
                triggersEnding: EndingType.FOX_CONQUEST
            }
        ]
    },
    {
        id: 'fox_chain_final_peace',
        title: '평화 협정',
        description: '오랜 협상 끝에 마침내...',
        category: 'chain',
        chainId: 'fox_invasion',
        chainStep: 7,
        requiresFlag: 'chose_diplomacy',
        options: [
            {
                id: 'fox_peace_alliance',
                text: '동맹을 맺는다',
                moneyChange: -500,
                popChange: 10,
                happinessChange: 15,
                outcomeDescription: '역사적인 평화! 여우와 오리가 친구가 되었습니다!',
                endsChain: true,
                setFlag: 'fox_alliance'
            },
            {
                id: 'fox_peace_neutral',
                text: '불가침 조약만',
                moneyChange: -200,
                popChange: 0,
                happinessChange: 10,
                outcomeDescription: '서로 건드리지 않기로 했습니다.',
                endsChain: true,
                setFlag: 'fox_neutral'
            }
        ]
    },

    // ========== 수상한 알 체인 ==========
    {
        id: 'egg_chain_0',
        title: '알이 움직인다',
        description: '황금알에서 미세한 움직임이 느껴집니다!',
        category: 'chain',
        chainId: 'mysterious_egg',
        chainStep: 0,
        requiresFlag: 'has_golden_egg',
        options: [
            {
                id: 'egg_warm',
                text: '따뜻하게 품어준다',
                moneyChange: -20,
                popChange: 0,
                happinessChange: 5,
                outcomeDescription: '알이 더 활발하게 움직입니다!',
                continuesChain: 'mysterious_egg'
            },
            {
                id: 'egg_study',
                text: '연구한다',
                moneyChange: -50,
                popChange: 0,
                happinessChange: 0,
                outcomeDescription: '신비한 에너지가 느껴집니다...',
                continuesChain: 'mysterious_egg',
                setFlag: 'studied_egg'
            }
        ]
    },
    {
        id: 'egg_chain_3',
        title: '금이 간다!',
        description: '알 표면에 금이 가기 시작합니다!',
        category: 'chain',
        chainId: 'mysterious_egg',
        chainStep: 3,
        options: [
            {
                id: 'egg_help',
                text: '부화를 돕는다',
                moneyChange: 0,
                popChange: 0,
                happinessChange: 5,
                outcomeDescription: '조심스럽게 껍데기를 벗겨줍니다...',
                continuesChain: 'mysterious_egg'
            },
            {
                id: 'egg_wait',
                text: '지켜본다',
                moneyChange: 0,
                popChange: 0,
                happinessChange: 0,
                outcomeDescription: '자연에 맡깁니다.',
                continuesChain: 'mysterious_egg'
            }
        ]
    },
    {
        id: 'egg_chain_final',
        title: '탄생!',
        description: '알에서 무언가가 태어났습니다!',
        category: 'chain',
        chainId: 'mysterious_egg',
        chainStep: 5,
        options: [
            {
                id: 'egg_born_golden',
                text: '(황금빛이 퍼진다)',
                moneyChange: 1000,
                popChange: 1,
                happinessChange: 30,
                outcomeDescription: '전설의 황금 오리가 태어났습니다!',
                endsChain: true,
                setFlag: 'golden_duck_born'
            }
        ]
    },

    // ========== 하늘로의 여정 체인 ==========
    {
        id: 'sky_chain_0',
        title: '철새의 제안',
        description: '철새가 먼 하늘 너머의 이야기를 들려줍니다.',
        category: 'chain',
        chainId: 'sky_migration',
        chainStep: 0,
        requiresCharacter: 'migrant_bird',
        options: [
            {
                id: 'sky_interested',
                text: '더 듣고 싶다',
                moneyChange: 0,
                popChange: 0,
                happinessChange: 5,
                outcomeDescription: '구름 위의 왕국... 정말 있을까?',
                continuesChain: 'sky_migration'
            },
            {
                id: 'sky_decline',
                text: '여기가 좋다',
                moneyChange: 0,
                popChange: 0,
                happinessChange: 0,
                outcomeDescription: '뿌리 내린 곳이 가장 좋은 법.',
                endsChain: true
            }
        ]
    },
    {
        id: 'sky_chain_3',
        title: '비행 훈련',
        description: '하늘을 나는 훈련이 시작됩니다.',
        category: 'chain',
        chainId: 'sky_migration',
        chainStep: 3,
        options: [
            {
                id: 'sky_train_hard',
                text: '열심히 훈련',
                moneyChange: -100,
                popChange: -2,
                happinessChange: 5,
                outcomeDescription: '날개가 점점 강해집니다!',
                continuesChain: 'sky_migration',
                setFlag: 'flight_trained'
            },
            {
                id: 'sky_train_cancel',
                text: '포기한다',
                moneyChange: 0,
                popChange: 0,
                happinessChange: -5,
                outcomeDescription: '역시 오리는 물 위가...',
                endsChain: true
            }
        ]
    },
    {
        id: 'sky_chain_final',
        title: '대이주',
        description: '드디어 하늘로 떠날 준비가 되었습니다!',
        category: 'chain',
        chainId: 'sky_migration',
        chainStep: 6,
        requiresFlag: 'flight_trained',
        options: [
            {
                id: 'sky_go',
                text: '새로운 세계로!',
                moneyChange: 0,
                popChange: 0,
                happinessChange: 0,
                outcomeDescription: '구름 위의 왕국을 향해!',
                endsChain: true,
                setFlag: 'ascended',
                triggersEnding: EndingType.SKY_KINGDOM
            },
            {
                id: 'sky_stay',
                text: '마음이 바뀌었다',
                moneyChange: 0,
                popChange: 0,
                happinessChange: 10,
                outcomeDescription: '이곳이 나의 집!',
                endsChain: true
            }
        ]
    },

    // ========== 심해 탐험 체인 ==========
    {
        id: 'sea_chain_0',
        title: '바다의 부름',
        description: '해적 펠리컨이 바다 너머 이야기를 합니다.',
        category: 'chain',
        chainId: 'sea_expedition',
        chainStep: 0,
        requiresCharacter: 'pirate_pelican',
        options: [
            {
                id: 'sea_curious',
                text: '탐험대를 조직한다',
                moneyChange: -300,
                popChange: 0,
                happinessChange: 5,
                outcomeDescription: '모험의 시작!',
                continuesChain: 'sea_expedition'
            },
            {
                id: 'sea_no',
                text: '바다는 위험해',
                moneyChange: 0,
                popChange: 0,
                happinessChange: 0,
                outcomeDescription: '연못이 제일이야.',
                endsChain: true
            }
        ]
    },
    {
        id: 'sea_chain_4',
        title: '폭풍우!',
        description: '대양에서 거대한 폭풍을 만났습니다!',
        category: 'chain',
        chainId: 'sea_expedition',
        chainStep: 4,
        options: [
            {
                id: 'sea_through',
                text: '돌파한다!',
                moneyChange: -200,
                popChange: -5,
                happinessChange: -10,
                outcomeDescription: '간신히 살아남았습니다...',
                continuesChain: 'sea_expedition'
            },
            {
                id: 'sea_back',
                text: '돌아간다',
                moneyChange: -100,
                popChange: -2,
                happinessChange: -5,
                outcomeDescription: '포기도 용기입니다.',
                endsChain: true
            }
        ]
    },
    {
        id: 'sea_chain_final',
        title: '신비의 섬',
        description: '마침내 전설의 섬에 도착했습니다!',
        category: 'chain',
        chainId: 'sea_expedition',
        chainStep: 7,
        options: [
            {
                id: 'sea_settle',
                text: '새로운 왕국을 세운다',
                moneyChange: 0,
                popChange: 0,
                happinessChange: 0,
                outcomeDescription: '심해 왕국의 탄생!',
                endsChain: true,
                setFlag: 'sea_kingdom',
                triggersEnding: EndingType.SEA_KINGDOM
            },
            {
                id: 'sea_return',
                text: '보물을 가지고 돌아간다',
                moneyChange: 5000,
                popChange: 0,
                happinessChange: 30,
                outcomeDescription: '전설의 보물을 가지고 영웅 귀환!',
                endsChain: true,
                setFlag: 'sea_treasure'
            }
        ]
    },

    // ========== 진실의 길 체인 (True Ending 루트) ==========
    {
        id: 'true_chain_0',
        title: '백조의 예언',
        description: '백조가 수수께끼 같은 예언을 남깁니다.',
        category: 'chain',
        chainId: 'true_path',
        chainStep: 0,
        requiresCharacter: 'mystic_swan',
        options: [
            {
                id: 'true_follow',
                text: '예언을 따른다',
                moneyChange: 0,
                popChange: 0,
                happinessChange: 0,
                outcomeDescription: '"모든 생명을 사랑하라..."',
                continuesChain: 'true_path'
            }
        ]
    },
    {
        id: 'true_chain_5',
        title: '깨달음',
        description: '모든 것이 연결되어 있음을 깨닫습니다.',
        category: 'chain',
        chainId: 'true_path',
        chainStep: 5,
        options: [
            {
                id: 'true_enlighten',
                text: '진리를 받아들인다',
                moneyChange: 0,
                popChange: 0,
                happinessChange: 50,
                outcomeDescription: '마음이 평화로워집니다.',
                continuesChain: 'true_path',
                setFlag: 'enlightened'
            }
        ]
    },
    {
        id: 'true_chain_final',
        title: '완성',
        description: '긴 여정의 끝에 서있습니다.',
        category: 'chain',
        chainId: 'true_path',
        chainStep: 9,
        requiresFlag: 'enlightened',
        options: [
            {
                id: 'true_ending',
                text: '마지막 문을 연다',
                moneyChange: 0,
                popChange: 0,
                happinessChange: 0,
                outcomeDescription: '모든 것의 진실...',
                endsChain: true,
                triggersEnding: EndingType.TRUE_ENDING
            }
        ]
    }
];

// ============================================
// 모든 이벤트 합치기
// ============================================

export const ALL_EVENTS: GameEvent[] = [
    ...DAILY_EVENTS,
    ...SEASONAL_EVENTS,
    ...CRISIS_EVENTS,
    ...OPPORTUNITY_EVENTS,
    ...CHARACTER_EVENTS,
    ...MILESTONE_EVENTS,
    ...CHAIN_EVENTS
];

// 카테고리별 이벤트 수
export const EVENT_COUNTS = {
    daily: DAILY_EVENTS.length,
    seasonal: SEASONAL_EVENTS.length,
    crisis: CRISIS_EVENTS.length,
    opportunity: OPPORTUNITY_EVENTS.length,
    character: CHARACTER_EVENTS.length,
    milestone: MILESTONE_EVENTS.length,
    chain: CHAIN_EVENTS.length,
    total: ALL_EVENTS.length
};
